import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { clearAllPipelineIntents } from "@/hooks/pipelineIntents";
import {
  decodeJwt,
  getUserFromToken,
  isTokenExpired,
  tokenExpiresInMs,
} from "./jwt";
import {
  clearOAuthState,
  getProviderById,
  getProviders,
  isAuthConfigured,
  redirectToLogout,
  refreshAccessToken,
} from "./oidc";
import { tokenStore } from "./tokenStore";
import type { AuthState, AuthUser, RefreshToken } from "./types";

type AuthAction =
  | { type: "LOGIN_SUCCESS"; user: AuthUser; token: string }
  | { type: "TOKEN_REFRESHED"; user: AuthUser; token: string }
  | { type: "LOGOUT" };

function authReducer(_state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "LOGIN_SUCCESS":
    case "TOKEN_REFRESHED":
      return {
        status: "authenticated",
        user: action.user,
        token: action.token,
      };
    case "LOGOUT":
      return { status: "unauthenticated" };
  }
}

const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 10_000;
const FALLBACK_REFRESH_DELAY_MS = 4 * 60 * 1000;
const MAX_TIMEOUT_MS = 2_147_483_647; // setTimeout 32-bit signed int limit (~24.8 days)
// 1 initial attempt + 2 retries = 3 total attempts
const MAX_REFRESH_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

type AuthContextValue = {
  state: AuthState;
  logout: () => void;
  completeLogin: (user: AuthUser, token: string) => void;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

function persistTokens(
  accessToken: string,
  refreshToken?: string,
  idToken?: string,
): void {
  tokenStore.setAccessToken(accessToken);
  if (refreshToken) tokenStore.setRefreshToken(refreshToken);
  if (idToken) tokenStore.setIdToken(idToken);
  else tokenStore.clearIdToken();
}

function refreshDelayFromStoredToken(): number {
  const idToken = tokenStore.getIdToken();
  const accessToken = tokenStore.getAccessToken();
  const jwt = idToken ?? accessToken;
  if (!jwt) return FALLBACK_REFRESH_DELAY_MS;

  const expiresIn = tokenExpiresInMs(jwt);
  if (expiresIn === null) return FALLBACK_REFRESH_DELAY_MS;

  const d = expiresIn - REFRESH_MARGIN_MS;
  return Math.min(Math.max(d, MIN_REFRESH_DELAY_MS), MAX_TIMEOUT_MS);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function resolveStoredProvider(): OidcProvider | null {
  const id = tokenStore.getProviderId();
  if (id) {
    const provider = getProviderById(id);
    if (provider) return provider;
  }
  const all = getProviders();
  return all.length === 1 ? all[0] : null;
}

async function executeRefresh(
  provider: OidcProvider,
  refreshToken: RefreshToken,
): Promise<{ user: AuthUser; token: string }> {
  const tokens = await refreshAccessToken(provider, refreshToken);
  const jwt = tokens.id_token ?? tokens.access_token;
  const payload = decodeJwt(jwt);
  if (!payload) throw new Error("Failed to decode refreshed token");
  const user = getUserFromToken(payload);
  if (!user) throw new Error("Failed to extract user from refreshed token");

  persistTokens(tokens.access_token, tokens.refresh_token, tokens.id_token);
  return { user, token: tokens.access_token };
}

async function attemptRefresh(
  refreshToken: RefreshToken,
  maxAttempts: number,
): Promise<{ user: AuthUser; token: string }> {
  let lastError: unknown;
  const provider = resolveStoredProvider();
  if (!provider) throw new Error("Unable to resolve OIDC provider for refresh");

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await executeRefresh(provider, refreshToken);
    } catch (err) {
      lastError = err;
      if (attempt < maxAttempts) {
        await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
      }
    }
  }

  throw lastError;
}

function extractUserFromStoredTokens(): {
  user: AuthUser;
  token: string;
} | null {
  const idToken = tokenStore.getIdToken();
  const accessToken = tokenStore.getAccessToken();
  if (!accessToken) return null;

  const jwt = idToken ?? accessToken;
  const payload = decodeJwt(jwt);
  if (!payload) return null;
  const user = getUserFromToken(payload);
  if (!user) return null;
  return { user, token: accessToken };
}

function tryLoginFromStoredTokens(): {
  user: AuthUser;
  token: string;
} | null {
  const idToken = tokenStore.getIdToken();
  const accessToken = tokenStore.getAccessToken();
  if (!accessToken) return null;

  const jwt = idToken ?? accessToken;
  if (isTokenExpired(jwt)) return null;
  return extractUserFromStoredTokens();
}

function logoutAndClear(): void {
  tokenStore.clearAll();
  clearOAuthState();
}

async function performRefresh(
  dispatch: React.Dispatch<AuthAction>,
): Promise<boolean> {
  const storedRefresh = tokenStore.getRefreshToken();
  if (!storedRefresh) {
    tokenStore.clearAll();
    dispatch({ type: "LOGOUT" });
    return false;
  }

  try {
    const { user, token } = await attemptRefresh(
      storedRefresh,
      MAX_REFRESH_ATTEMPTS,
    );
    dispatch({ type: "TOKEN_REFRESHED", user, token });
    return true;
  } catch (error) {
    console.error("Token refresh failed:", error);
    logoutAndClear();
    dispatch({ type: "LOGOUT" });
    return false;
  }
}

function resolveStorageEvent(
  event: StorageEvent,
  skipDuringRefresh: boolean,
): AuthAction | null {
  if (event.key === null) return { type: "LOGOUT" };
  if (!event.key.startsWith("3t-bridge:auth:")) return null;
  if (skipDuringRefresh) return null;

  const result = extractUserFromStoredTokens();
  if (!result) return { type: "LOGOUT" };

  return { type: "TOKEN_REFRESHED", user: result.user, token: result.token };
}

const DISABLED_CONTEXT: AuthContextValue = {
  state: { status: "unauthenticated" },
  logout: () => {},
  completeLogin: () => {},
};

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!isAuthConfigured()) {
    return (
      <AuthContext.Provider value={DISABLED_CONTEXT}>
        {children}
      </AuthContext.Provider>
    );
  }

  return <AuthProviderInner>{children}</AuthProviderInner>;
}

function AuthProviderInner({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, { status: "loading" });
  const queryClient = useQueryClient();
  const initMutexRef = useRef(false);
  const refreshMutexRef = useRef(false);
  const refreshTimerRef = useRef<number | null>(null);

  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current != null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const doRefreshRef = useRef<() => Promise<void>>(async () => {});

  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current != null) clearTimeout(refreshTimerRef.current);
    const ms = refreshDelayFromStoredToken();
    refreshTimerRef.current = window.setTimeout(() => {
      doRefreshRef.current();
    }, ms);
  }, []);

  const doRefresh = useCallback(async () => {
    if (refreshMutexRef.current) return;
    refreshMutexRef.current = true;
    try {
      const ok = await performRefresh(dispatch);
      if (!ok) {
        stopRefreshTimer();
        return;
      }
      startRefreshTimer();
    } finally {
      refreshMutexRef.current = false;
    }
  }, [stopRefreshTimer, startRefreshTimer]);

  doRefreshRef.current = doRefresh;

  const completeLogin = useCallback(
    (user: AuthUser, token: string) => {
      dispatch({ type: "LOGIN_SUCCESS", user, token });
      startRefreshTimer();
    },
    [startRefreshTimer],
  );

  const logout = useCallback(() => {
    stopRefreshTimer();
    // Drop the cross-session query cache so a later login can't briefly seed the
    // UI with this session's data (logout may not trigger a full page reload).
    queryClient.clear();
    // Same reason for the module-level start/stop intents, which live outside the
    // query cache and would otherwise overlay the next session's jobs UI.
    clearAllPipelineIntents();
    const idToken = tokenStore.getIdToken();
    const providerId = tokenStore.getProviderId();
    const provider = providerId ? getProviderById(providerId) : undefined;
    logoutAndClear();
    if (!provider) {
      dispatch({ type: "LOGOUT" });
      return;
    }
    // Redirect to IdP logout first, then dispatch LOGOUT as a fallback
    // if the page doesn't navigate away (e.g. no end_session_endpoint,
    // popup blocker, or test environment).
    redirectToLogout(provider, idToken)
      .then((redirected) => {
        // Only dispatch when no IdP redirect was initiated (no end_session_endpoint).
        // When redirected, the pagehide/timeout safety net handles the transition.
        if (!redirected) dispatch({ type: "LOGOUT" });
      })
      .catch((err) => {
        console.error("OIDC logout redirect failed:", err);
        dispatch({ type: "LOGOUT" });
      });
    // Safety net: if the browser navigates to the IdP logout page,
    // this timeout never fires. If navigation is blocked, it ensures
    // the UI transitions to unauthenticated after a short delay.
    const timerId = window.setTimeout(() => {
      window.removeEventListener("pagehide", handlePageHide);
      dispatch({ type: "LOGOUT" });
    }, 2000);
    function handlePageHide() {
      window.clearTimeout(timerId);
      window.removeEventListener("pagehide", handlePageHide);
    }
    window.addEventListener("pagehide", handlePageHide);
  }, [stopRefreshTimer, queryClient]);

  useEffect(() => {
    if (initMutexRef.current) return;
    initMutexRef.current = true;

    async function initializeAuth() {
      if (!tokenStore.getAccessToken()) {
        dispatch({ type: "LOGOUT" });
        return;
      }

      const cached = tryLoginFromStoredTokens();
      if (cached) {
        dispatch({
          type: "LOGIN_SUCCESS",
          user: cached.user,
          token: cached.token,
        });
        startRefreshTimer();
        return;
      }

      const storedRefresh = tokenStore.getRefreshToken();
      if (!storedRefresh) {
        tokenStore.clearAll();
        dispatch({ type: "LOGOUT" });
        return;
      }

      const refreshed = await performRefresh(dispatch);
      if (refreshed) startRefreshTimer();
    }

    initializeAuth();
  }, [startRefreshTimer]);

  useEffect(() => {
    function handleStorageEvent(event: StorageEvent) {
      const action = resolveStorageEvent(event, refreshMutexRef.current);
      if (!action) return;

      if (action.type === "LOGOUT") {
        stopRefreshTimer();
      } else {
        startRefreshTimer();
      }
      dispatch(action);
    }

    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [startRefreshTimer, stopRefreshTimer]);

  useEffect(() => {
    return () => {
      stopRefreshTimer();
    };
  }, [stopRefreshTimer]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, logout, completeLogin }),
    [state, logout, completeLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
